import { useMemo, useState } from "react";
import { db } from "../firebaseConfig";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";

import { getDocs, collection, query, where } from "firebase/firestore";

import Header from "../components/HeaderV2";
import DatePicker from "../components/datepicker";
import common from "../style/screens/Common.module.css";
import style from "../style/screens/ExcelV2.module.css";
import { KioskType, useKiosk } from "../KioskContext";

type StatusFilter = "ACTIVE" | "INACTIVE" | "FULL" | "ALL";

const ExcelV2 = () => {
    const partnerId = sessionStorage.getItem("partnerId");
    const { kiosks } = useKiosk();
    const [selected, setSelected] = useState<string[]>([]);
    const [startDate, setStartDate] = useState(startOfDay(new Date()));
    const [endDate, setEndDate] = useState(endOfDay(new Date()));
    const [search, setSearch] = useState<string>("");
    const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");
    const [loading, setLoading] = useState<boolean>(false);
    const [message, setMessage] = useState<string>("");

    const filteredKiosks = useMemo(() => {
        const keyword = search.toLowerCase();
        return kiosks.filter((kiosk) => {
            const matchesStatus = statusFilter === "ALL" || kiosk.status === statusFilter;
            const matchesKeyword =
                search === "" ||
                kiosk.id.toLowerCase().includes(keyword) ||
                (kiosk.name ?? "").toLowerCase().includes(keyword) ||
                (kiosk.address ?? "").toLowerCase().includes(keyword);

            return matchesStatus && matchesKeyword;
        });
    }, [kiosks, search, statusFilter]);

    const visibleIds = filteredKiosks.map((kiosk) => kiosk.docId);
    const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.includes(id));
    const invalidDateRange = startDate.getTime() > endDate.getTime();
    const fileName = makeFileName(startDate, endDate);
    const canDownload = selected.length > 0 && !invalidDateRange && !loading;

    const toggleVisible = () => {
        if (allVisibleSelected) {
            setSelected((prev) => prev.filter((id) => !visibleIds.includes(id)));
        } else {
            setSelected((prev) => Array.from(new Set([...prev, ...visibleIds])));
        }
    };

    const clearSelected = () => {
        setSelected([]);
    };

    const setPreset = (preset: "TODAY" | "LAST_7" | "THIS_MONTH" | "LAST_MONTH") => {
        const today = new Date();
        if (preset === "TODAY") {
            setStartDate(startOfDay(today));
            setEndDate(endOfDay(today));
        }
        if (preset === "LAST_7") {
            const start = new Date(today);
            start.setDate(start.getDate() - 6);
            setStartDate(startOfDay(start));
            setEndDate(endOfDay(today));
        }
        if (preset === "THIS_MONTH") {
            setStartDate(startOfDay(new Date(today.getFullYear(), today.getMonth(), 1)));
            setEndDate(endOfDay(today));
        }
        if (preset === "LAST_MONTH") {
            const start = new Date(today.getFullYear(), today.getMonth() - 1, 1);
            const end = new Date(today.getFullYear(), today.getMonth(), 0);
            setStartDate(startOfDay(start));
            setEndDate(endOfDay(end));
        }
        setMessage("");
    };

    const createExcel = async () => {
        setMessage("");

        if (!partnerId) {
            setMessage("partnerId가 없습니다. 다시 로그인해주세요.");
            return;
        }

        if (invalidDateRange) {
            setMessage("시작일을 종료일 이전으로 선택해주세요.");
            return;
        }

        if (!selected.length) {
            setMessage("키오스크를 1개 이상 선택해주세요.");
            return;
        }

        setLoading(true);

        try {
            const chunkSize = 30;
            let allReceipts: any[] = [];

            for (let i = 0; i < selected.length; i += chunkSize) {
                const chunk = selected.slice(i, i + chunkSize);

                const snapshot = await getDocs(
                    query(
                        collection(db, "receipt"),
                        where("partnerId", "==", partnerId),
                        where("timestamp", ">=", startDate.getTime()),
                        where("timestamp", "<=", endDate.getTime()),
                        where("kiosk", "in", chunk)
                    )
                );

                const receipts = snapshot.docs.map((doc) => {
                    const data = doc.data();
                    const kioskInfo = kiosks.find((k) => k.docId === data.kiosk);
                    return {
                        id: doc.id,
                        inputAmt: data.inputAmt,
                        inputType: data.inputType,
                        kiosk: kioskInfo ? kioskInfo.name : data.kiosk,
                        owner: data.owner,
                        timestamp: data.timestamp,
                    };
                });

                allReceipts = allReceipts.concat(receipts);
            }

            if (allReceipts.length === 0) {
                setMessage("선택한 조건에 해당하는 데이터가 없습니다.");
                return;
            }

            downloadReceiptsAsExcel(allReceipts);
            setMessage(`${allReceipts.length.toLocaleString()}건의 데이터를 엑셀로 생성했습니다.`);
        } catch (err) {
            console.error(err);
            setMessage("엑셀 생성 중 에러가 발생했습니다.");
        } finally {
            setLoading(false);
        }
    };

    const downloadReceiptsAsExcel = (receipts: any[]) => {
        receipts.sort((a, b) => {
            if (a.kiosk < b.kiosk) return -1;
            if (a.kiosk > b.kiosk) return 1;
            return a.timestamp - b.timestamp;
        });

        const headerMap: Record<string, string> = {
            id: "영수증 ID",
            inputAmt: "투입 수량",
            kiosk: "키오스크 ID",
            owner: "유저 ID",
            timestamp: "시각",
            carbonReduction: "탄소 절감량 (gCO₂)",
        };

        const orderedKeys: string[] = ["kiosk", "timestamp", "inputAmt", "carbonReduction", "owner", "id"];

        const data = receipts.map((r) => {
            const row: any = {};
            orderedKeys.forEach((key) => {
                if (key === "timestamp") {
                    row[headerMap[key]] = new Date(r.timestamp).toLocaleString("ko-KR");
                } else if (key === "carbonReduction") {
                    const reduction = r.inputAmt * 66;
                    row[headerMap[key]] = `${reduction.toLocaleString()} g`;
                } else {
                    row[headerMap[key]] = r[key];
                }
            });
            return row;
        });

        const worksheet = XLSX.utils.json_to_sheet(data);
        const workbook = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(workbook, worksheet, "Receipts");

        const excelBuffer = XLSX.write(workbook, {
            bookType: "xlsx",
            type: "array",
        });

        const blob = new Blob([excelBuffer], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });

        saveAs(blob, fileName);
    };

    return (
        <>
            <Header />
            <div className={common["container"]}>
                <div className={common["wrapper"]}>
                    <h4 className={common["text--title"]}>엑셀 출력</h4>
                    <p className={common["text--copy"]}>
                        날짜와 키오스크를 선택하여 페트병 회수 데이터를 다운로드합니다.
                    </p>

                    <section className={style["export-panel"]}>
                        <div className={style["panel-header"]}>
                            <div>
                                <p className={style["section-kicker"]}>출력 조건</p>
                                <h5 className={style["panel-title"]}>엑셀 생성 조건</h5>
                                <p className={style["panel-copy"]}>
                                    {selected.length}개 키오스크 · {formatDate(startDate)} ~ {formatDate(endDate)}
                                </p>
                            </div>
                            <button className={style["button--primary"]} onClick={createExcel} disabled={!canDownload}>
                                {loading ? "생성 중" : "엑셀 생성"}
                            </button>
                        </div>

                        <div className={style["preset-row"]}>
                            <button onClick={() => setPreset("TODAY")}>오늘</button>
                            <button onClick={() => setPreset("LAST_7")}>최근 7일</button>
                            <button onClick={() => setPreset("THIS_MONTH")}>이번 달</button>
                            <button onClick={() => setPreset("LAST_MONTH")}>지난 달</button>
                        </div>

                        <div className={style["wrapper--date-time"]}>
                            <DatePicker label="시작일" value={startDate} onChange={(date) => setStartDate(startOfDay(date))} />
                            <DatePicker label="종료일" value={endDate} onChange={(date) => setEndDate(endOfDay(date))} />
                        </div>

                        <div className={style["condition-grid"]}>
                            <InfoCard label="선택 키오스크" value={`${selected.length}개`} />
                            <InfoCard label="출력 파일명" value={fileName} />
                        </div>

                        {(message || invalidDateRange) && (
                            <div className={[style["status-message"], invalidDateRange && style["status-message--error"]].join(" ")}>
                                {invalidDateRange ? "시작일을 종료일 이전으로 선택해주세요." : message}
                            </div>
                        )}
                    </section>

                    <section className={style["kiosk-panel"]}>
                        <div className={style["panel-header"]}>
                            <div>
                                <p className={style["section-kicker"]}>대상 선택</p>
                                <h5 className={style["panel-title"]}>키오스크 선택</h5>
                                <p className={style["panel-copy"]}>
                                    총 {kiosks.length}대 중 {filteredKiosks.length}대 표시 · {selected.length}대 선택
                                </p>
                            </div>
                            <div className={style["panel-actions"]}>
                                <button className={style["button--secondary"]} onClick={toggleVisible} disabled={visibleIds.length === 0}>
                                    {allVisibleSelected ? "현재 목록 선택 해제" : "현재 목록 전체선택"}
                                </button>
                                <button className={style["button--secondary"]} onClick={clearSelected} disabled={selected.length === 0}>
                                    전체 해제
                                </button>
                            </div>
                        </div>

                        <div className={style["toolbar"]}>
                            <input
                                id="search-kiosk"
                                className={common["input--kiosk-filter"]}
                                type="text"
                                placeholder="아이디, 이름 또는 주소로 검색"
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                            <div className={style["segment"]}>
                                <button
                                    className={statusFilter === "ALL" ? style["segment--active"] : ""}
                                    onClick={() => setStatusFilter("ALL")}
                                >
                                    전체
                                </button>
                                <button
                                    className={statusFilter === "ACTIVE" ? style["segment--active"] : ""}
                                    onClick={() => setStatusFilter("ACTIVE")}
                                >
                                    운영중
                                </button>
                                <button
                                    className={statusFilter === "INACTIVE" ? style["segment--active"] : ""}
                                    onClick={() => setStatusFilter("INACTIVE")}
                                >
                                    비활성
                                </button>
                                <button
                                    className={statusFilter === "FULL" ? style["segment--active"] : ""}
                                    onClick={() => setStatusFilter("FULL")}
                                >
                                    수거필요
                                </button>
                            </div>
                        </div>

                        <div className={style["wrapper--kiosk-list"]}>
                            <div className={[style["kiosk-row"], style["kiosk-row--head"]].join(" ")}>
                                <p>선택</p>
                                <p>키오스크</p>
                                <p>ID</p>
                                <p>주소</p>
                                <p>상태</p>
                            </div>
                            {filteredKiosks.length === 0 && <div className={style["empty-state"]}>검색 결과가 없습니다.</div>}
                            {filteredKiosks.map((kiosk) => (
                                <KioskList
                                    key={kiosk.docId}
                                    kiosk={kiosk}
                                    selected={selected.includes(kiosk.docId)}
                                    onClick={() =>
                                        setSelected((prev) =>
                                            prev.includes(kiosk.docId)
                                                ? prev.filter((i) => i !== kiosk.docId)
                                                : [...prev, kiosk.docId]
                                        )
                                    }
                                />
                            ))}
                        </div>
                    </section>
                </div>
            </div>
        </>
    );
};

const KioskList = ({ kiosk, selected, onClick }: { kiosk: KioskType; selected: boolean; onClick: () => void }) => (
    <div
        className={[style["unit--kiosk-list"], selected && style["unit--kiosk-list--selected"]]
            .filter(Boolean)
            .join(" ")}
        onClick={onClick}
    >
        <div className={style["kiosk-row"]}>
            <div
                className={[
                    style["unit--kiosk-list__checkbox"],
                    selected && style["unit--kiosk-list__checkbox--selected"],
                ]
                    .filter(Boolean)
                    .join(" ")}
            >
                {selected && <span className={style["unit--kiosk-list__checkmark"]}></span>}
            </div>
            <p className={style["kiosk-name"]}>{kiosk.name || "이름 없음"}</p>
            <p className={style["kiosk-meta"]}>{kiosk.id}</p>
            <p className={style["kiosk-meta"]}>{kiosk.address || "주소 없음"}</p>
            <p>
                <span className={[style["status"], style[`status--${kiosk.status.toLowerCase()}`]].join(" ")}>
                    {statusLabel(kiosk.status)}
                </span>
            </p>
        </div>
    </div>
);

const InfoCard = ({ label, value }: { label: string; value: string }) => (
    <div className={style["info-card"]}>
        <span>{label}</span>
        <strong>{value}</strong>
    </div>
);

const startOfDay = (date: Date) => {
    const next = new Date(date);
    next.setHours(0, 0, 0, 0);
    return next;
};

const endOfDay = (date: Date) => {
    const next = new Date(date);
    next.setHours(23, 59, 59, 999);
    return next;
};

const formatDate = (date: Date) =>
    `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, "0")}-${String(date.getDate()).padStart(2, "0")}`;

const makeFileName = (startDate: Date, endDate: Date) => `kiosk_report__${formatDate(startDate)}_${formatDate(endDate)}.xlsx`;

const statusLabel = (status: KioskType["status"]) => {
    switch (status) {
        case "ACTIVE":
            return "운영중";
        case "INACTIVE":
            return "비활성";
        case "FULL":
            return "수거필요";
        default:
            return "등록대기";
    }
};

export default ExcelV2;
